import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Bell,
  BellOff,
  Calendar,
  ChevronDown,
  Clock,
  CornerUpLeft,
  Forward,
  MailOpen,
  Paperclip,
  ReplyAll,
  Star,
  Trash2,
  UserPlus,
  UserRound,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { Conversation, Inbox, Message } from '../../../api/types';
import { ApiError } from '../../../api/envelope';
import { ContactAvatar } from '../../../components/ContactAvatar';
import { RefreshButton } from '../../../components/RefreshButton';
import { capabilitiesOf } from '../../../lib/channelCapabilities';
import {
  addressIn,
  addressLabel,
  contactEmailOf,
  reachedMany,
  recipientsOf,
  sameSubject,
  senderOf,
  subjectOf,
} from '../../../lib/emailRecipients';
import { labelTint } from '../../../lib/labelTint';
import { useTimeFormat } from '../../../lib/tenantTime';
import { useDismissOnOutside } from '../../../lib/useDismiss';
import { useLoad } from '../../../lib/useLoad';
import { AiBookingBox, type BookingSaved } from '../booking/AiBookingBox';
import { bookerLinksFor } from '../booking/bookerLinks';
import { BookingNoteText } from '../booking/BookingNoteText';
import { ThreadBookings } from '../booking/ThreadBookings';
import { useThreadBookings } from '../booking/useThreadBookings';
import { CallButton } from '../CallButton';
import { Composer, ComposerBotOwned, ComposerReadOnly } from '../Composer';
import { useComms } from '../context';
import { contactDisplayName } from '../dispatchIdentity';
import { composerDraftKey } from '../drafts';
import { EmailBody } from '../EmailBody';
import { LabelPicker } from '../LabelPicker';
import { ThreadSkeleton } from '../Skeletons';
import { AssignMenuItems, MuteMenuItems, NotifyStrips, SnoozeMenuItems } from '../ThreadControls';
import { assignTitle, useConversationMute, useThreadCallOptions } from '../threadMenus';
import { useContactDetail } from '../useContactDetail';
import { useModuleRefresh } from '../useModuleRefresh';
import { useThread } from '../useThread';
import type { MailActions } from './actions';
import { mailSubjectOf } from './latestBookings';
import { MailContact } from './MailContact';
import { ourRef } from '../../../lib/reservationRef';

/** Gmail collapses everything but the newest; a short thread reads whole. */
const EXPAND_ALL_UNDER = 3;

/** One envelope line, drawn only when the header actually carried that field. */
function HeaderRow({ label, values }: { label: string; values: string[] }) {
  if (!values.length) return null;
  return (
    <div>
      <span>{label}:</span> {values.join(', ')}
    </div>
  );
}

/**
 * One message in the vertical thread — a one-line summary until it is opened.
 *
 * The collapsed line is the mail-client convention and it is load-bearing on a
 * long thread: an agent taking over a twenty-message conversation wants the
 * shape of it first and the words of one of them second.
 */
function ThreadMessage({
  message,
  open,
  onToggle,
  contactName,
  contactEmail,
  ownEmail,
  threadSubject,
  onReply,
  onDraftBooking,
  onDelete,
}: {
  message: Message;
  open: boolean;
  onToggle: () => void;
  contactName: string;
  /** The contact's own address — what an outgoing reply that named nobody went
   *  to, which is every ordinary reply. Without it the header reads "to me". */
  contactEmail: string | null;
  /** This mailbox's address, which is the From on everything we sent. */
  ownEmail: string | null;
  /** The thread's title, so a message carrying the same one does not repeat it. */
  threadSubject: string;
  onReply: () => void;
  onDraftBooking?: (id: number) => void;
  onDelete?: (id: number) => void;
}) {
  const { messageTime, fmtDateTime } = useTimeFormat();
  const [showTo, setShowTo] = useState(false);
  const incoming = message.messageType === 'incoming';
  const deleted = message.contentAttributes.deleted === true;
  const html = message.contentAttributes.email?.htmlContent?.sanitized;
  const asEmail = !deleted && (message.contentType === 'incoming_email' || !!html);
  const who = incoming ? contactName : message.senderType === 'bot' ? 'Assistant' : 'You';
  const when = message.externalCreatedAt ?? message.createdAt;

  /*
   * The whole envelope, not the half of it a reply needs.
   *
   * This expander IS where an agent goes to see who a message really reached,
   * so every field the wire carries has to be in it: the From (an inbound mail
   * whose sender differs from the name above it — a portal that sends as
   * support@ on a customer's behalf — is exactly the case worth seeing), the
   * Bcc, and a Subject that differs from the thread's, which is how a forward
   * announces itself. `fallbackTo` is what stops an ordinary reply, which names
   * no recipients of its own, reading as though it went nowhere.
   */
  const { to, cc, bcc } = recipientsOf(message, contactEmail);
  const from = incoming ? senderOf(message) : ownEmail ? [ownEmail] : [];
  // An inbound blind copy is stripped before it reaches us, so anything there
  // was addressed to this mailbox and says nothing; on our OWN message it is a
  // fact the sender needs back. Same rule as the three-pane bubble.
  const blind = incoming ? [] : bcc;
  const subject = subjectOf(message);
  const ownSubject = subject && !sameSubject(subject, threadSubject) ? subject : '';
  const reached = [...to, ...cc, ...blind];
  const fromAddress = message.private ? null : incoming ? (addressIn(from[0] ?? '') ?? contactEmail) : ownEmail;

  if (message.messageType === 'activity') {
    return <div className="gm-activity">{message.content}</div>;
  }

  if (!open) {
    return (
      <button type="button" className="gm-msg collapsed" onClick={onToggle}>
        <ContactAvatar name={who} size={28} />
        <span className="gm-msg-who">{who}</span>
        <span className="gm-msg-peek">{message.content?.replace(/\s+/g, ' ').slice(0, 160)}</span>
        {message.attachments.length > 0 && <Paperclip size={12} />}
        <span className="gm-msg-when">{messageTime(when)}</span>
      </button>
    );
  }

  return (
    // The id is how the booking strip finds this message to scroll to it.
    <div className={`gm-msg open${message.private ? ' note' : ''}`} data-msg-id={message.id}>
      <div className="gm-msg-head">
        <ContactAvatar name={who} size={34} />
        <div className="gm-msg-id">
          <div className="gm-msg-line">
            <span className="gm-msg-who">{who}</span>
            {/* Gmail's "Name <address>": which address wrote is half of who
                wrote, and a portal sending as support@ on a customer's behalf
                is exactly the mail where the name alone misleads. Not on a
                note, which no mailbox ever sent. */}
            {fromAddress && <span className="gm-msg-addr">&lt;{fromAddress}&gt;</span>}
            {message.private && <span className="gm-msg-tag">Internal note</span>}
          </div>
          <button
            type="button"
            className="gm-msg-to"
            onClick={() => setShowTo(!showTo)}
            title="Who this reached"
          >
            {/* Names rather than addresses, where the header supplied one:
                `Bob <b@x>` is a chip that says Bob everywhere else in this
                console. The count covers Cc and our own Bcc too — a line
                reading "and 1 more" over a mail that reached four is the
                summary being wrong about the only thing it says. */}
            {reached.length
              ? `to ${addressLabel(reached[0])}${
                  reached.length > 1 ? ` and ${reached.length - 1} more` : ''
                }`
              : 'to me'}
            <ChevronDown size={11} />
          </button>
          {showTo && (
            <div className="gm-msg-headers">
              <HeaderRow label="from" values={from} />
              <HeaderRow label="to" values={to} />
              <HeaderRow label="cc" values={cc} />
              <HeaderRow label="bcc" values={blind} />
              {ownSubject && (
                <div>
                  <span>subject:</span> {ownSubject}
                </div>
              )}
              <div>
                <span>date:</span> {fmtDateTime(when)}
              </div>
            </div>
          )}
        </div>
        <span className="gm-msg-when" title={fmtDateTime(when)}>
          {messageTime(when)}
        </span>
        <button type="button" className="gm-icon" title="Reply" onClick={onReply}>
          <CornerUpLeft size={15} />
        </button>
        {onDelete && !deleted && message.id > 0 && (
          <button
            type="button"
            className="gm-icon"
            title="Delete this message"
            onClick={() => onDelete(message.id)}
          >
            <Trash2 size={14} />
          </button>
        )}
        <button type="button" className="gm-icon" title="Collapse" onClick={onToggle}>
          <ChevronDown size={15} style={{ transform: 'rotate(180deg)' }} />
        </button>
      </div>
      <div className={`gm-msg-body${deleted ? ' deleted' : ''}`}>
        {asEmail ? <EmailBody message={message} /> : <BookingNoteText message={message} />}
      </div>
      {message.attachments.length > 0 && (
        <div className="gm-msg-files">
          {message.attachments.map((attachment) => (
            <a
              key={attachment.id}
              className="gm-msg-file"
              href={attachment.dataUrl ?? undefined}
              target="_blank"
              rel="noreferrer"
            >
              <Paperclip size={12} />
              {attachment.fileName ?? attachment.fileType}
            </a>
          ))}
        </div>
      )}
      {onDraftBooking && incoming && !message.private && !deleted && message.id > 0 && (
        <button
          type="button"
          className="gm-msg-book"
          onClick={() => onDraftBooking(message.id)}
        >
          <Calendar size={12} />
          Draft reservation from this message
        </button>
      )}
    </div>
  );
}

/**
 * The reading view: one conversation, full width, over the list rather than
 * beside it. Gmail's shape — a toolbar of verbs, the subject, the thread
 * stacked vertically, and the reply box at the foot behind its three buttons.
 *
 * Two rows of controls, split by what they act on. The TOOLBAR is Gmail's own
 * verbs, the ones that move the thread somewhere (archive, the bin, unread,
 * snooze) and mostly hand the agent back to the list. The cluster beside the
 * STAR is the three-pane header's: calling the contact, who holds the thread,
 * whether it reaches this agent, a refresh and the profile — the controls a
 * mail client has no word for, which is why they sit apart from Gmail's.
 *
 * Everything it can do it does through the SAME pieces the three-pane layout
 * uses (useThread, Composer, AiBookingBox, ThreadControls), so the two layouts
 * cannot drift apart on what a reply is, what a booking draft reads, or what
 * "Assign to me" does.
 */
export function GmailThread({
  conversation,
  inbox,
  actions,
  onBack,
  onChanged,
  onSeen,
  contactOpen,
  onContactOpen,
}: {
  conversation: Conversation;
  inbox: Inbox | undefined;
  actions: MailActions;
  onBack: () => void;
  onChanged: () => void;
  /**
   * Zero the row's badge the way the three-pane layout does — through the
   * list, which also patches every cached filter. A reload alone rewrites
   * only the active filter's entry, so "open a thread, switch view and
   * straight back" repainted the badge the agent had just cleared.
   */
  onSeen: (displayId: number) => void;
  /** Whether the contact drawer is open beside this thread. */
  contactOpen: boolean;
  onContactOpen: (open: boolean) => void;
}) {
  const {
    source,
    agents,
    me,
    canned,
    templateBlocks,
    draftScope,
    sendDenied,
    noteSendDenied,
    canReply,
    labels: labelCatalog,
    reloadLabels,
  } = useComms();
  // Stable per source: the booking box keys its pick handler on it.
  const bookerLinks = useMemo(() => bookerLinksFor(source), [source]);
  const thread = useThread(source, conversation.id, noteSendDenied, conversation.botHandled);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  // What this thread has already produced in dispatch — the strip under the
  // toolbar, kept current by `booking.created`.
  const threadBookings = useThreadBookings(source, conversation.id);
  const contact = conversation.contact;
  // Who is writing, and what they last booked — the strip under the subject.
  // The same detail read the drawer makes (one request for both), and the same
  // reservation read the list chip makes, for this one contact.
  const detail = useContactDetail(source, contact.id);
  const latest = useLoad(() => source.api.listLatestContactBookings([contact.id]), [source, contact.id]);
  // Which reservation the subject names, when it names one — shown in place of the last one.
  const mailSubject = mailSubjectOf(conversation);
  const subjectRead = useLoad(
    () => (mailSubject ? source.api.listSubjectBookings([conversation.id]) : Promise.resolve(null)),
    [source, conversation.id, mailSubject],
  );
  const named = subjectRead.data?.payload.find((row) => row.conversationId === conversation.id) ?? null;
  const [touched, setTouched] = useState(false);
  /** Which of Gmail's three foot buttons opened the box, or none. */
  const [replying, setReplying] = useState<'reply' | 'replyAll' | 'forward' | null>(null);
  const [booking, setBooking] = useState<{ fromMessageId?: number; runId: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<'snooze' | 'assign' | 'mute' | 'call' | null>(null);
  // One ref for them all: only the open menu is mounted, so it is the one holding it.
  const menuRef = useDismissOnOutside<HTMLDivElement>(menu !== null, () => setMenu(null));

  const replyDenied = sendDenied || (inbox ? !canReply(inbox.id) : false);
  const caps = capabilitiesOf(inbox);
  const botOwns = conversation.status === 'pending' && conversation.assigneeIsBot;
  const conversationMute = useConversationMute(conversation.id);

  const subject =
    (typeof conversation.additionalAttributes.mailSubject === 'string' &&
      conversation.additionalAttributes.mailSubject.trim()) ||
    '(no subject)';
  const contactName = contactDisplayName(conversation.contact);

  const lastInbound = useMemo(
    () => [...thread.messages].reverse().find((m) => m.messageType === 'incoming'),
    [thread.messages],
  );

  // Who a reply is between. The pills read it for the two addresses the
  // envelope header needs (an ordinary reply names no recipients of its own,
  // and our own address is the From on everything we sent), and the composer
  // rebuilds the same thing for itself — it is keyed by conversation, so
  // passing it in would only give the two a way to disagree.
  const replyTargets = useMemo(
    () => ({
      contactEmail: contactEmailOf(conversation),
      ownEmail: inbox?.channelType === 'email' ? (inbox.channel.email ?? null) : null,
      lastInbound,
    }),
    [conversation, inbox, lastInbound],
  );

  // Which messages are open. Gmail's rule: the newest always, everything on a
  // short thread, and whatever the agent has since clicked. `touched` is what
  // keeps a collapse from being undone by the next re-render.
  const openIds = useMemo(() => {
    if (touched) return expanded;
    const readable = thread.messages.filter((m) => m.messageType !== 'activity');
    if (readable.length <= EXPAND_ALL_UNDER) return new Set(readable.map((m) => m.id));
    const last = readable[readable.length - 1];
    return new Set(last ? [last.id] : []);
  }, [touched, expanded, thread.messages]);

  const toggleMessage = (id: number) => {
    setTouched(true);
    setExpanded(() => {
      const next = new Set(openIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * The header cluster's runner, ThreadPane's shape: close the menu, report a
   * refusal in words (nothing on screen moves when the server says no), and
   * re-read the row on success.
   */
  const runAction = async (action: () => Promise<unknown>) => {
    setMenu(null);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not go through.');
    }
  };
  // Mute and unmute touch only the agent's own notification prefs — nothing on
  // the conversation changes, so nothing is re-read.
  const runQuiet = async (action: () => Promise<unknown>) => {
    setMenu(null);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not go through.');
    }
  };

  const callOptions = useThreadCallOptions({
    contact,
    inbox,
    onRun: (action) => void runAction(action),
    onError: setError,
  });

  /**
   * One click for the whole conversation: its messages, the row, and the
   * contact and reservation in the strip. Realtime keeps the first two
   * current; the strip is dispatch's to change, which nothing here hears of.
   */
  const refreshThread = () => {
    setMenu(null);
    setError(null);
    detail.reload();
    latest.reload();
    onChanged();
    void thread.reload().catch(() => setError('Could not refresh this conversation.'));
  };
  // The module-wide Refresh reaches the open thread through here, as it does
  // the three-pane one; the list answers it in InboxView.
  useModuleRefresh(() => {
    detail.reload();
    latest.reload();
    void thread.reload().catch(() => {});
  });

  /**
   * The whole set, every time — the endpoint replaces. It must REJECT for the
   * picker's optimistic tick to be dropped again, which is why it does not go
   * through `actions`: those are fire-and-forget over an optimistic list and
   * report their own failures.
   */
  const applyLabels = async (next: string[]) => {
    setError(null);
    await source.api.setConversationLabels(conversation.id, next);
    // A title typed in the picker is a label that did not exist a moment ago.
    reloadLabels();
    onChanged();
  };

  /**
   * Labels taken off from their chip's ×, hidden from the moment of the click.
   * The row is re-read only after the write, and a chip that sat there for
   * that round trip reads as a click that missed. An entry is forgotten once
   * the row agrees — adjusted during render rather than in an effect — so the
   * same label put back later is not hidden with it.
   */
  const [dropping, setDropping] = useState<readonly string[]>([]);
  const [labelsSeen, setLabelsSeen] = useState(conversation.labels);
  if (labelsSeen !== conversation.labels) {
    setLabelsSeen(conversation.labels);
    setDropping((current) => {
      const still = current.filter((title) => conversation.labels.includes(title));
      return still.length === current.length ? current : still;
    });
  }
  const shownLabels = conversation.labels.filter((title) => !dropping.includes(title));
  const removeLabel = (title: string) => {
    setDropping((current) => [...current, title]);
    applyLabels(shownLabels.filter((one) => one !== title)).catch((e: unknown) => {
      setDropping((current) => current.filter((one) => one !== title));
      setError(e instanceof ApiError ? e.message : 'Could not remove that label.');
    });
  };

  // Opening a thread reads it, exactly as the three-pane layout does.
  useEffect(() => {
    if ((conversation.unreadCount ?? 0) > 0) {
      onSeen(conversation.id);
      source.api.markConversationSeen(conversation.id).then(onChanged, () => {});
    }
    // Only when the CONVERSATION changes — re-running on every list refresh
    // would re-stamp a row whose badge is already zero, which is the write the
    // bulk endpoint goes out of its way to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id]);

  /**
   * The CRM-side record of a dispatch write: the row the booking strip reads,
   * plus the private note the service posts beside it. The booking already
   * exists by the time this runs, so a failure is reported and the booking is
   * left alone — creation is not idempotent.
   *
   * Simpler than ThreadPane's: this layout unmounts the whole reading view when
   * the agent leaves the thread, so there is no open-elsewhere case.
   */
  const recordBooking = async (saved: BookingSaved) => {
    setBooking(null);
    const kind = saved.kind === 'draft' ? 'draft' : saved.isQuote ? 'quote' : 'job';
    // What they last booked has just changed, as far as dispatch is concerned.
    latest.reload();
    try {
      const { booking: row, notePosted } = await source.api.recordConversationBooking(
        conversation.id,
        {
          kind,
          dispatchId: saved.dispatchId,
          bookerReference: saved.bookerReference,
          summary: saved.summary,
          pickupAt: saved.pickupAt,
          messageId: saved.messageId,
          trip: saved.trip,
        },
      );
      threadBookings.add(row);
      if (!notePosted) {
        setError(`Created ${ourRef(kind, row.reference)}, but the note couldn't be posted.`);
      }
    } catch {
      setError(
        `Created ${ourRef(kind, saved.dispatchId)} in dispatch, but this thread couldn't record it.`,
      );
    }
  };

  /**
   * Show the message a booking was drafted from. Gmail's reading view collapses
   * every message but the last, so this opens the one it lands on as well as
   * scrolling to it — a jump to a collapsed one-line row shows nothing.
   */
  const showMessage = (messageId: number) => {
    // `touched` as well as the set: without it the next render recomputes
    // `openIds` from Gmail's default rule and closes what was just opened.
    setTouched(true);
    setExpanded(() => {
      const next = new Set(openIds);
      next.add(messageId);
      return next;
    });
    // Next frame: the row has to be expanded before it has a position to
    // scroll to, or a collapsed one-liner is what gets centred.
    requestAnimationFrame(() => {
      const node = document.querySelector(`.gm-read-scroll [data-msg-id="${messageId}"]`);
      if (!node) {
        setError('That message is further back — load earlier messages to see it.');
        return;
      }
      setError(null);
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.classList.remove('is-located');
      void (node as HTMLElement).offsetWidth;
      node.classList.add('is-located');
    });
  };

  const deleteMessage = (id: number) => {
    void thread.remove(id).catch((e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not delete that message.');
    });
  };

  return (
    <div className="gm-read">
      <div className="gm-toolbar">
        <button type="button" className="gm-icon" title="Back to the list" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <span className="gm-toolbar-rule" />
        {/* Gmail's Archive turns into Move to Inbox on mail already archived,
            and ours does the same: a closed thread is reopened from here, and
            the agent stays on it — they reopened it to work on it. */}
        {conversation.status === 'resolved' ? (
          <button
            type="button"
            className="gm-icon"
            title="Move to inbox (reopen)"
            onClick={() => void runAction(() => source.api.toggleStatus(conversation.id, 'open'))}
          >
            <ArchiveRestore size={19} />
          </button>
        ) : (
          <button
            type="button"
            className="gm-icon"
            title="Archive (resolve)"
            onClick={() => {
              actions.archive(conversation.id);
              onBack();
            }}
          >
            <Archive size={19} />
          </button>
        )}
        <button
          type="button"
          className="gm-icon"
          title="Delete"
          onClick={() => {
            actions.remove(conversation.id);
            onBack();
          }}
        >
          <Trash2 size={19} />
        </button>
        <button
          type="button"
          className="gm-icon"
          title="Mark as unread"
          onClick={() => {
            actions.markUnread(conversation.id);
            onBack();
          }}
        >
          <MailOpen size={19} />
        </button>
        {/* Gmail's Snooze is a menu of times, and so is ours now — the same
            durations the three-pane header offers. Picking one leaves the
            thread, as Gmail's does: it is off the list until then. */}
        <div className="thr-head-menu">
          <button
            type="button"
            className={`gm-icon${conversation.status === 'snoozed' ? ' on' : ''}`}
            title={conversation.status === 'snoozed' ? 'Snoozed — snooze for longer' : 'Snooze'}
            onClick={() => setMenu(menu === 'snooze' ? null : 'snooze')}
          >
            <Clock size={19} />
          </button>
          {menu === 'snooze' && (
            <div className="thr-menu gm-menu-left" ref={menuRef}>
              <SnoozeMenuItems
                onSnooze={(hours) => {
                  setMenu(null);
                  actions.snooze(conversation.id, hours);
                  onBack();
                }}
              />
            </div>
          )}
        </div>
        {/* Where a mail client puts it, and the same control the three-pane
            header carries — one component, so the two layouts cannot come to
            disagree about what labelling a thread does. It hangs from its LEFT
            edge here: this far along a wide toolbar, a right-hung menu opens
            off the side of the pane. */}
        <LabelPicker
          key={conversation.id}
          applied={shownLabels}
          catalog={labelCatalog.map((label) => label.title)}
          disabled={replyDenied}
          className="gm-icon"
          iconSize={19}
          align="left"
          onApply={applyLabels}
          onError={setError}
        />
        <span className="gm-toolbar-rule" />
        {/* Ours, not Gmail's — the booking draft this product exists to offer,
            wearing the same button it wears in the three-pane layout. */}
        <button
          type="button"
          className="gm-book"
          disabled={replyDenied}
          title="Draft a reservation from this conversation with AI"
          onClick={() => setBooking((prev) => ({ runId: (prev?.runId ?? 0) + 1 }))}
        >
          <Calendar size={16} />
          Book now
        </button>
        <div className="gm-toolbar-fill" />
      </div>

      <ThreadBookings
        bookings={threadBookings.bookings}
        onOpenMessage={showMessage}
      />

      <div className="gm-read-scroll">
        <div className="gm-subject">
          <div className="gm-subject-title">
            <h1>{subject}</h1>
            {/* Gmail's label chips: every label, in its own colour, each with
                the × that takes it off. Read-only agents get the chips without
                the ×, as the picker is disabled for them too. */}
            {shownLabels.map((label) => (
              <span key={label} className="gm-label" style={labelTint(label)}>
                <span className="gm-label-text">{label}</span>
                {!replyDenied && (
                  <button
                    type="button"
                    className="gm-label-x"
                    title={`Remove the label ${label}`}
                    aria-label={`Remove the label ${label}`}
                    onClick={() => removeLabel(label)}
                  >
                    <X size={11} />
                  </button>
                )}
              </span>
            ))}
          </div>
          <div className="gm-subject-actions">
            <button
              type="button"
              className={`gm-icon gm-subject-star${conversation.starred ? ' on' : ''}`}
              title={conversation.starred ? 'Remove star' : 'Star'}
              onClick={() => actions.toggleStar(conversation.id, !conversation.starred)}
            >
              <Star size={19} style={conversation.starred ? { fill: 'currentColor' } : undefined} />
            </button>
            {/* The three-pane header's controls, right of the star. */}
            <CallButton
              options={callOptions}
              to={contact.phoneNumber ?? ''}
              open={menu === 'call'}
              onOpenChange={(next) => setMenu(next ? 'call' : null)}
            />
            <div className="thr-head-menu">
              <button
                type="button"
                className="gm-icon"
                // Named for what it does, whoever holds the thread: the holder is the
                // tooltip's to say (and the screen reader's description), since a name
                // that changed with every assignment is a control nobody can find by name.
                aria-label="Assign"
                title={assignTitle(conversation, agents, me?.dashBoardId)}
                onClick={() => setMenu(menu === 'assign' ? null : 'assign')}
              >
                <UserPlus size={18} />
              </button>
              {menu === 'assign' && (
                <div className="thr-menu" ref={menuRef}>
                  <AssignMenuItems
                    conversation={conversation}
                    inbox={inbox}
                    onRun={(action) => void runAction(action)}
                  />
                </div>
              )}
            </div>
            {/* Snooze is in the toolbar, where Gmail keeps it, so the bell here
                is the other half of the header's clock menu on its own. */}
            <div className="thr-head-menu">
              <button
                type="button"
                className={`gm-icon${conversationMute ? ' on' : ''}`}
                title={
                  conversationMute
                    ? 'Notifications muted for this conversation'
                    : 'Mute notifications for this conversation'
                }
                onClick={() => setMenu(menu === 'mute' ? null : 'mute')}
              >
                {conversationMute ? <BellOff size={18} /> : <Bell size={18} />}
              </button>
              {menu === 'mute' && (
                <div className="thr-menu" ref={menuRef}>
                  <MuteMenuItems
                    conversationId={conversation.id}
                    mute={conversationMute}
                    onDone={(action) => void runQuiet(action)}
                  />
                </div>
              )}
            </div>
            <RefreshButton
              className="gm-icon"
              size={18}
              label={null}
              title="Refresh this conversation"
              refreshing={thread.refreshing}
              busy={thread.loading}
              onClick={refreshThread}
            />
            <button
              type="button"
              className={`cbtn gm-profile${contactOpen ? ' on' : ''}`}
              title={contactOpen ? 'Hide contact details' : 'Show contact details'}
              onClick={() => onContactOpen(!contactOpen)}
            >
              <UserRound size={16} />
              <span className="cbtn-label">Profile</span>
            </button>
          </div>
        </div>

        <MailContact
          conversation={conversation}
          inbox={inbox}
          detail={detail}
          latest={latest}
          named={named}
          namedPending={!!mailSubject && subjectRead.loading && !subjectRead.data}
          onOpenProfile={() => onContactOpen(true)}
        />

        <NotifyStrips
          conversation={conversation}
          inbox={inbox}
          onRun={(action) => void runAction(action)}
        />

        {error && <div className="comms-error-note">{error}</div>}

        {thread.hasOlder && (
          <button
            type="button"
            className="gm-older"
            onClick={thread.loadOlder}
            disabled={thread.loadingOlder}
          >
            {thread.loadingOlder ? 'Loading earlier messages…' : 'Load earlier messages'}
          </button>
        )}
        {thread.loading && thread.messages.length === 0 && <ThreadSkeleton />}
        {thread.messages.map((message) => (
          <ThreadMessage
            key={message.id}
            message={message}
            open={openIds.has(message.id)}
            onToggle={() => toggleMessage(message.id)}
            contactName={contactName}
            contactEmail={replyTargets.contactEmail}
            ownEmail={replyTargets.ownEmail}
            threadSubject={subject}
            onReply={() => setReplying('reply')}
            onDraftBooking={
              replyDenied
                ? undefined
                : (id) => setBooking((prev) => ({ fromMessageId: id, runId: (prev?.runId ?? 0) + 1 }))
            }
            onDelete={replyDenied ? undefined : deleteMessage}
          />
        ))}

        {booking && !replyDenied && (
          <AiBookingBox
            key={`${conversation.id}:${booking.runId}`}
            conversation={conversation}
            fromMessageId={booking.fromMessageId}
            draftBooking={source.api.draftBooking}
            checkDuplicates={source.api.checkBookingDuplicates}
            bookerLinks={bookerLinks}
            dispatch={source.dispatch}
            onForbidden={noteSendDenied}
            onClose={() => setBooking(null)}
            onSaved={(saved: BookingSaved) => void recordBooking(saved)}
          />
        )}

        {/* Gmail's foot: three buttons that become the box. Rendered inside the
            scroller rather than pinned, which is what a mail client does — the
            reply belongs at the end of the thread you just read. */}
        {replyDenied ? (
          <ComposerReadOnly reason="Your level on this channel is view — you can read but not reply." />
        ) : botOwns ? (
          <ComposerBotOwned
            onTakeOver={() => {
              void source.api.toggleStatus(conversation.id, 'open').then(onChanged, () => {});
            }}
          />
        ) : replying ? (
          <Composer
            key={`${conversation.id}:${replying}`}
            conversation={conversation}
            inbox={inbox}
            canned={canned}
            blocks={templateBlocks}
            lastInboundAt={lastInbound?.externalCreatedAt ?? lastInbound?.createdAt ?? null}
            lastInbound={lastInbound}
            threadSubject={subject}
            draftKey={composerDraftKey(draftScope, conversation.id)}
            openAs={replying}
            api={{
              getDraft: source.api.getDraft,
              saveDraft: source.api.saveDraft,
              draftReply: source.api.draftReply,
              presignUpload: source.api.presignUpload,
              putToS3: source.api.putToS3,
            }}
            onSend={thread.send}
            onSendAndClose={async () => {
              actions.archive(conversation.id);
              onBack();
            }}
          />
        ) : (
          <div className="gm-reply-pills">
            <button type="button" onClick={() => setReplying('reply')}>
              <CornerUpLeft size={15} />
              Reply
            </button>
            {/* Both extra pills need a second field to put anybody in, which is
                what `caps.cc` answers — on SMS there is one recipient and the
                thread ends in Reply alone, as it should.

                Reply all is then offered whenever the mail REACHED more than
                one party, not only when a reply-all would add somebody new —
                see reachedMany. This button opens a composer rather than
                sending, and a shared mailbox is so often one of the addressees
                that the stricter rule hides it on half the mail an operator
                reads. The composer's own Reply-all SEND button keeps the
                stricter rule, because that one is a send. */}
            {caps.cc && reachedMany(lastInbound) && (
              <button type="button" onClick={() => setReplying('replyAll')}>
                <ReplyAll size={15} />
                Reply all
              </button>
            )}
            {caps.cc && (
              <button type="button" onClick={() => setReplying('forward')}>
                <Forward size={15} />
                Forward
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
